/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwiseIcon } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * AutoUpdater — forces the app to stay up-to-date at all times.
 *
 * Two independent update channels:
 *  1. Service-worker channel: the browser detects a newer `sw.js` or any
 *     cached asset and we auto-apply it via `skipWaiting` + `controllerchange`.
 *  2. Backend-version channel: we poll `/api/version` every 60s and, if the
 *     `build_id` changes between the first load and now, we auto-reload after
 *     giving the user a 5-second toast with a manual override.
 */
const POLL_INTERVAL_MS = 60 * 1000;
const SW_UPDATE_INTERVAL_MS = 60 * 1000;

const AutoUpdater = () => {
  const [initialBuild, setInitialBuild] = useState(null);
  const reloadingRef = useRef(false);

  const triggerReload = (reason) => {
    if (reloadingRef.current) return;
    reloadingRef.current = true;
    toast.info("Updating LUCRÜ…", { description: reason, duration: 2500 });
    setTimeout(() => window.location.reload(), 1200);
  };

  useEffect(() => {
    let cancelled = false;

    // ---- Backend version watchdog ----
    const fetchVersion = async () => {
      try {
        const r = await api.get("/version", { timeout: 8000 });
        return r.data?.build_id || r.data?.version;
      } catch {
        return null;
      }
    };

    (async () => {
      const b = await fetchVersion();
      if (!cancelled && b) setInitialBuild(b);
    })();

    const poll = setInterval(async () => {
      const b = await fetchVersion();
      if (!b || cancelled) return;
      if (initialBuild && b !== initialBuild) {
        triggerReload("A new build is live");
      }
    }, POLL_INTERVAL_MS);

    // ---- Service-worker auto-upgrade ----
    let swUpdateTimer;
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistration("/sw.js").then((reg) => {
        if (!reg) return;
        const kickWaiting = () => {
          if (reg.waiting) {
            reg.waiting.postMessage({ type: "SKIP_WAITING" });
          }
        };
        reg.addEventListener("updatefound", () => {
          const sw = reg.installing;
          if (!sw) return;
          sw.addEventListener("statechange", () => {
            if (sw.state === "installed" && navigator.serviceWorker.controller) {
              kickWaiting();
            }
          });
        });
        swUpdateTimer = setInterval(() => reg.update().catch(() => {}), SW_UPDATE_INTERVAL_MS);
      });

      // When the SW takes control after skipWaiting, reload the page.
      let hasReloaded = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (hasReloaded) return;
        hasReloaded = true;
        triggerReload("New version installed");
      });
    }

    // ---- Also check when the tab becomes visible again ----
    const onVisible = async () => {
      if (document.visibilityState !== "visible") return;
      const b = await fetchVersion();
      if (b && initialBuild && b !== initialBuild) triggerReload("New build detected");
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      clearInterval(poll);
      if (swUpdateTimer) clearInterval(swUpdateTimer);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBuild]);

  return null;
};

export default AutoUpdater;
